import React, { useCallback, useEffect } from 'react';

import { useAppContext } from '@prisjakt/ui-app-context';
import { usePriceRowContext } from '../../../price-row-context';

import { Popover, Alignment } from '@prisjakt/ui/popover';
import { BodySmallText } from '@prisjakt/ui/text/variants';
import { GoToStoreButton } from '../go-to-store-button';
import { StockPriceInfo } from '../stock-price-info';
import { StoreInfoColumn } from '../store-info-column';

import { AlternativePrice } from 'src/types';
import { ITableLinkProps } from '../../types';

import {
  StyledEndColumn,
  StyledLink,
  StyledMiddleColumn,
  StyledProductName,
  StyledProductNameColumn,
  StyledTopMiddleRow,
} from './styles';

type Props = {
  alternativeOffer?: AlternativePrice;
  isAlternative?: boolean;
  priceRowRef?: React.RefObject<HTMLDivElement>;
  setShowDisabledInfo?: React.Dispatch<React.SetStateAction<boolean>>;
  showDisabledInfo?: boolean;
  tableLinkProps: ITableLinkProps;
};

export const UnfeaturedColumns: React.FC<React.PropsWithChildren<
  React.PropsWithChildren<Props>
>> = props => {
  const {
    alternativeOffer,
    isAlternative = false,
    priceRowRef,
    setShowDisabledInfo,
    showDisabledInfo,
    tableLinkProps,
  } = props;
  const { t } = useAppContext();
  const { offer, forceMobileView } = usePriceRowContext();

  const { priceId } = offer;

  const name = alternativeOffer?.name || offer.name;

  const handleOutSideClick = useCallback(
    (e: MouseEvent) => {
      if (
        priceRowRef &&
        priceRowRef.current &&
        priceRowRef.current.contains(e.target as HTMLElement)
      ) {
        return;
      }
      if (setShowDisabledInfo) setShowDisabledInfo(false);
    },
    [priceRowRef, setShowDisabledInfo]
  );

  // componentWillUnmount
  useEffect(() => {
    return () => {
      window.removeEventListener('click', handleOutSideClick);
    };
  }, [handleOutSideClick]);

  // Callback for showDisabledInfo state change
  useEffect(() => {
    if (!setShowDisabledInfo) return;
    if (showDisabledInfo) {
      window.addEventListener('click', handleOutSideClick);
    } else {
      window.removeEventListener('click', handleOutSideClick);
    }
  }, [handleOutSideClick, setShowDisabledInfo, showDisabledInfo]);

  return (
    <>
      <StyledLink {...tableLinkProps} aria-describedby={`click-out-${priceId}`}>
        {!isAlternative && <StoreInfoColumn />}
        <StyledMiddleColumn>
          <StyledTopMiddleRow $forceMobileView={forceMobileView}>
            <StyledProductNameColumn
              $forceMobileView={Boolean(forceMobileView)}
            >
              <StyledProductName $forceMobileView={forceMobileView}>
                {name}
              </StyledProductName>
            </StyledProductNameColumn>
            <StockPriceInfo alternativeOffer={alternativeOffer} />
          </StyledTopMiddleRow>
        </StyledMiddleColumn>

        <StyledEndColumn $forceMobileView={forceMobileView}>
          <GoToStoreButton />
        </StyledEndColumn>
      </StyledLink>

      <Popover
        id={`click-out-${priceId}`}
        visible={Boolean(showDisabledInfo)}
        width={250}
        horizontalAlign={Alignment.Center}
        verticalAlign={Alignment.Center}
        onClose={() =>
          setShowDisabledInfo && setShowDisabledInfo(!showDisabledInfo)
        }
      >
        <BodySmallText>{t('product', 'transparency.store-info')}</BodySmallText>
      </Popover>
    </>
  );
};
